import { useEffect, useState, useContext, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../../../../../utils/axiosInstance";
import ContactPersonsTable from "./ContactPersonsTable";
import AddContactPersonForm from "./AddContactPersonForm";
import EditContactPersonForm from "./EditContactPersonForm";
import { toast, confirmDialog } from "../../../../../components/Toast/toast";
import { AuthContext } from "../../../../../context/AuthContext";
import { MODULES, isReadOnly } from "../../../../../utils/constants/permissions";
import { btn } from "../../../../../components/Button/buttonStyles";

const buildPrimaryUserRow = (vendor) => {
  if (!vendor) return null;
  const primary = vendor.primary_users || {};
  return {
    id: `primary-${vendor.id}`,
    isPrimary: true,
    first_name: primary.first_name || "",
    last_name: primary.last_name || "",
    email: vendor.auth?.email || "",
    gender: primary.gender || "",
    phone: primary.phone || "",
    teams_id: primary.teams_id || "",
    zoom_id: primary.zoom_id || "",
    position: "Primary User",
    is_active: !!vendor.auth?.is_active,
  };
};

const ContactPersonsPage = ({ vendorId, vendor }) => {
  const [contactPersons, setContactPersons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useContext(AuthContext);
  const readOnly = isReadOnly(user, MODULES.VENDORS);

  const action = searchParams.get("action");
  const editId = searchParams.get("id");

  const fetchContacts = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get(`vendor/contact-persons`, {
        params: { vendor_id: vendorId },
        withCredentials: true,
      });
      setContactPersons(res.data.data);
    } catch (err) {
      console.error(err);
      setError("Failed to fetch contact persons");
    } finally {
      setLoading(false);
    }
  }, [vendorId]);

  useEffect(() => {
    fetchContacts();
  }, [vendorId, fetchContacts]);

  const setAction = (nextAction, id) => {
    const next = new URLSearchParams(searchParams);
    if (nextAction) {
      next.set("action", nextAction);
    } else {
      next.delete("action");
    }
    if (id !== undefined && id !== null) {
      next.set("id", String(id));
    } else {
      next.delete("id");
    }
    setSearchParams(next, { replace: true });
  };

  const handleDelete = async (id) => {
    if (!await confirmDialog("Are you sure you want to delete this contact person?")) return;
    try {
      await api.delete(`vendor/contact-persons/${id}`, { withCredentials: true });
      fetchContacts();
    } catch (err) {
      toast.error("Failed to delete contact person");
    }
  };

  const handleAdd = async (data) => {
    await api.post("vendor/contact-persons", { ...data, vendor_id: vendorId });
    setAction(null);
    fetchContacts();
  };

  const handleUpdate = async (id, data) => {
    await api.put(`vendor/contact-persons/${id}`, data, { withCredentials: true });
    setAction(null);
    fetchContacts();
  };

  if (loading)
    return <div className="text-center mt-10 text-gray-500">Loading...</div>;

  if (error)
    return <div className="text-center mt-10 text-danger-600">{error}</div>;

  if (action === "add") {
    return (
      <AddContactPersonForm
        onCancel={() => setAction(null)}
        onSave={handleAdd}
      />
    );
  }

  if (action === "edit" && editId) {
    const person = contactPersons.find((p) => String(p.id) === String(editId));
    if (!person) {
      return (
        <div className="text-center mt-10 text-gray-500">
          Contact person not found.
        </div>
      );
    }
    return (
      <EditContactPersonForm
        person={person}
        onCancel={() => setAction(null)}
        onSave={handleUpdate}
      />
    );
  }

  const primaryRow = buildPrimaryUserRow(vendor);
  const tableRows = primaryRow
    ? [primaryRow, ...contactPersons]
    : contactPersons;

  return (
    <div className="space-y-5">
      <section>
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
            Contact Persons
          </h3>
          {!readOnly && (
            <button
              onClick={() => setAction("add")}
              className={btn("primary", "sm")}
            >
              Add Contact Person
            </button>
          )}
        </div>

        <ContactPersonsTable
          persons={tableRows}
          readOnly={readOnly}
          onEdit={(person) => setAction("edit", person.id)}
          onDelete={handleDelete}
        />
      </section>
    </div>
  );
};

export default ContactPersonsPage;
